// /admin/recipes: every recipe, newest first, with filters for flagged, drafts, scans and links.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { exportRecipes, getRecipes } from '../../../api/adminV2'
import { Chips, ListSearch, Pager, PAGE_SIZE, useListParams } from '../list'
import {
  formatDate,
  LoadError,
  Loading,
  Pill,
  RECIPE_SOURCE,
  saveFile,
  useFeedback,
  useLoad,
} from '../ui'

const FILTERS = [
  ['all', 'All'],
  ['flagged', 'Flagged'],
  ['published', 'Published'],
  ['draft', 'Drafts'],
  ['scan', 'Scanned cards'],
  ['link', 'From links'],
]

const Recipes = () => {
  const navigate = useNavigate()
  const { toast, toastError } = useFeedback()
  const { read, page, update, key } = useListParams({ filter: 'all' })
  const filter = read('filter')
  const q = read('q')
  const [downloading, setDownloading] = useState(false)

  const { data, error, loading, reload } = useLoad(
    () => getRecipes({ filter, q: q || undefined, page, size: PAGE_SIZE }),
    [key]
  )
  const rows = data?.data || []

  const download = () => {
    if (!rows.length) return
    setDownloading(true)
    exportRecipes({ ids: rows.map((r) => r.id) })
      .then((bytes) => {
        saveFile(bytes, 'Morsel recipes.xlsx')
        toast(`Downloaded ${rows.length} ${rows.length === 1 ? 'recipe' : 'recipes'}`)
      })
      .catch((e) => toastError(e, 'The spreadsheet could not be made.'))
      .finally(() => setDownloading(false))
  }

  return (
    <>
      <div className="a-head">
        <div>
          <h1>Recipes</h1>
          <p className="a-sub">Every recipe in Morsel, newest first.</p>
        </div>
        <button
          type="button"
          className="a-btn"
          onClick={download}
          disabled={!rows.length || downloading}
          aria-disabled={!rows.length || downloading}
          title="Downloads the recipes shown on this page as a spreadsheet"
        >
          {downloading ? 'Preparing…' : 'Download list'}
        </button>
      </div>
      <section className="a-panel">
        <Chips
          options={FILTERS}
          value={filter}
          counts={data?.counts || {}}
          onChange={(value) => update({ filter: value })}
        />
        <div className="a-toolbar">
          <ListSearch
            value={q}
            placeholder="Search by recipe or author name"
            onChange={(value) => update({ q: value }, { replace: !!q })}
          />
          {loading && data ? <span className="a-muted a-small-text">Loading…</span> : null}
        </div>
        {error ? (
          <LoadError error={error} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : (
          <>
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Recipe</th>
                    <th>Status</th>
                    <th>How it was added</th>
                    <th>Group</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((r) => (
                      <tr
                        className="a-click"
                        key={r.id}
                        onClick={() => navigate(`/admin/recipes/${r.id}`)}
                      >
                        <td className="a-who">
                          <b>{r.name || 'Untitled recipe'}</b>
                          {r.author_name ? <span>by {r.author_name}</span> : null}
                        </td>
                        <td>
                          <Pill status={r.status_key} />
                        </td>
                        <td className="a-nowrap">{RECIPE_SOURCE[r.source] || ''}</td>
                        <td>
                          {(r.groups || []).length ? (
                            r.groups.join(', ')
                          ) : (
                            <span className="a-muted">None</span>
                          )}
                        </td>
                        <td className="a-nowrap">{formatDate(r.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="a-muted">
                        {q ? `No recipes match "${q}" here.` : 'No recipes match this filter.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pager data={data} page={page} onPage={(p) => update({ page: p })} />
          </>
        )}
      </section>
    </>
  )
}

export default Recipes
